import { Box, Button } from '@ff-it/ui';
import { DropIndicator } from '@atlaskit/pragmatic-drag-and-drop-react-drop-indicator/box';
import clsx from 'clsx';
import { handleBtnClassname } from 'components/DND/DragHandle';
import { useDragMonitor, useDraggable } from 'components/DND';
import { useId, useRef, type CSSProperties, type ReactNode } from 'react';
import * as styles from './DiscountTreeEditor.css';
import type { DiscountRuleBase, DiscountTreeNode as AuthoredDiscountTreeNode } from './sparseTree';

export type DiscountColumn<Rule extends DiscountRuleBase, Context = unknown> = {
  id: string;
  header: ReactNode;
  group?: { id: string; header: ReactNode };
  width?: string;
  align?: 'start' | 'end';
  renderRule: (rule: Rule) => ReactNode;
  renderBranch?: (node: AuthoredDiscountTreeNode<Rule, Context>) => ReactNode;
};

export type DiscountRulePresentation = {
  label: ReactNode;
  ariaLabel: string;
  description?: ReactNode;
  diagnostic?: ReactNode;
};

export type DiscountBranchPresentation = {
  suffix?: ReactNode;
  actions?: ReactNode;
};

export type DiscountTreeSection<Rule extends DiscountRuleBase, Context = unknown> = {
  id: string;
  title?: ReactNode;
  description?: ReactNode;
  variant?: 'default' | 'attention';
  includes: (node: AuthoredDiscountTreeNode<Rule, Context>, rule: Rule) => boolean;
  /** Persisted roots can exist before any rules have been authored. */
  includesNode?: (node: AuthoredDiscountTreeNode<Rule, Context>) => boolean;
};

export type DiscountTreeEditorProps<Rule extends DiscountRuleBase, Context = unknown> = {
  nodes: readonly AuthoredDiscountTreeNode<Rule, Context>[];
  columns: readonly DiscountColumn<Rule, Context>[];
  presentRule: (rule: Rule) => DiscountRulePresentation;
  presentBranch?: (node: AuthoredDiscountTreeNode<Rule, Context>) => DiscountBranchPresentation | undefined;
  /** Opt-in compact presentation for a single unconditional rule. */
  inlineRule?: (node: AuthoredDiscountTreeNode<Rule, Context>) => Rule | undefined;
  actionsWidth?: string;
  sections?: readonly DiscountTreeSection<Rule, Context>[];
  emptyText?: ReactNode;
  readOnly?: boolean;
  disabled?: boolean;
  onEdit?: (node: AuthoredDiscountTreeNode<Rule, Context>, rule: Rule) => void;
  createAction?: ReactNode;
  onReorder?: (
    node: AuthoredDiscountTreeNode<Rule, Context>,
    source: Rule,
    target: Rule,
    edge: 'top' | 'bottom',
  ) => void | Promise<void>;
};
function filterSectionNodes<Rule extends DiscountRuleBase, Context = unknown>(
  nodes: readonly AuthoredDiscountTreeNode<Rule, Context>[],
  section: DiscountTreeSection<Rule, Context>,
): AuthoredDiscountTreeNode<Rule, Context>[] {
  return nodes.flatMap((node) => {
    const rules = node.rules.filter((rule) => section.includes(node, rule));
    const children = filterSectionNodes(node.children, section);
    return rules.length || children.length || section.includesNode?.(node) ? [{ ...node, rules, children }] : [];
  });
}

type ColumnRun<Rule extends DiscountRuleBase, Context = unknown> = {
  start: number;
  columns: readonly DiscountColumn<Rule, Context>[];
  group?: { id: string; header: ReactNode };
};

function columnRuns<Rule extends DiscountRuleBase, Context = unknown>(
  columns: readonly DiscountColumn<Rule, Context>[],
): ColumnRun<Rule, Context>[] {
  const runs: ColumnRun<Rule, Context>[] = [];
  columns.forEach((column, index) => {
    const previous = runs.at(-1);
    if (previous && previous.group?.id === column.group?.id && column.group) {
      runs[runs.length - 1] = { ...previous, columns: [...previous.columns, column] };
      return;
    }
    runs.push({ start: index, columns: [column], group: column.group });
  });
  return runs;
}

function GenericHeader<Rule extends DiscountRuleBase, Context = unknown>({
  columns,
}: {
  columns: readonly DiscountColumn<Rule, Context>[];
}) {
  const grouped = columns.some((column) => column.group);
  if (!grouped) {
    return (
      <div className={styles.header} role="row">
        <div className={styles.cell} role="columnheader">
          <Box flexGrow={1}>Target / rule</Box>
        </div>
        {columns.map((column) => (
          <div
            key={column.id}
            className={styles.value}
            role="columnheader"
            style={{ justifyContent: column.align === 'start' ? 'flex-start' : undefined }}
          >
            {column.header}
          </div>
        ))}
        <div role="columnheader" aria-label="Actions" className={styles.actions} />
      </div>
    );
  }

  return (
    <div className={styles.groupedHeader} role="row">
      <div className={styles.cell} role="columnheader" style={{ gridRow: 'span 2' }}>
        <Box flexGrow={1}>Target / rule</Box>
      </div>
      {columnRuns(columns).map((run) => (
        <div
          key={`group-${run.start}`}
          className={run.group ? styles.groupHeading : styles.cell}
          role={run.group ? 'columnheader' : undefined}
          aria-hidden={!run.group}
          style={{ gridColumn: `${run.start + 2} / span ${run.columns.length}`, gridRow: 1 }}
        >
          {run.group?.header}
        </div>
      ))}
      {columns.map((column, index) => (
        <div
          key={column.id}
          className={styles.groupedColumnHeading}
          role="columnheader"
          aria-label={
            typeof column.group?.header === 'string' && typeof column.header === 'string'
              ? `${column.group.header} · ${column.header}`
              : undefined
          }
          style={{
            gridColumn: index + 2,
            gridRow: 2,
            justifyContent: column.align === 'start' ? 'flex-start' : undefined,
          }}
        >
          {column.header}
        </div>
      ))}
      <div
        role="columnheader"
        aria-label="Actions"
        className={styles.actions}
        style={{ gridColumn: columns.length + 2, gridRow: 'span 2' }}
      />
    </div>
  );
}

function GenericRuleRow<Rule extends DiscountRuleBase, Context = unknown>({
  node,
  rule,
  index,
  depth,
  instanceId,
  columns,
  presentRule,
  readOnly,
  disabled,
  openEdit,
  onReorder,
  variant,
}: {
  node: AuthoredDiscountTreeNode<Rule, Context>;
  rule: Rule;
  index: number;
  depth: number;
  instanceId: string;
  columns: readonly DiscountColumn<Rule, Context>[];
  presentRule: (rule: Rule) => DiscountRulePresentation;
  readOnly: boolean;
  disabled: boolean;
  openEdit?: (node: AuthoredDiscountTreeNode<Rule, Context>, rule: Rule) => void;
  onReorder?: DiscountTreeEditorProps<Rule, Context>['onReorder'];
  variant: 'default' | 'attention';
}) {
  const presentation = presentRule(rule);
  const sortable = !!onReorder && variant !== 'attention' && !readOnly && !disabled && node.rules.length > 1;
  const { elementRef, handleRef, isDragging, closestEdge } = useDraggable(index, !sortable, instanceId);
  return (
    <div
      ref={elementRef}
      className={clsx(styles.row, variant === 'attention' && styles.attentionRow)}
      data-tree-row
      role="row"
      aria-label={presentation.ariaLabel}
      aria-level={depth + 2}
      style={{ opacity: isDragging ? 0.5 : undefined }}
    >
      {closestEdge && <DropIndicator edge={closestEdge} />}
      <div role="gridcell" className={styles.label} style={{ '--depth': depth + 1 } as CSSProperties}>
        <span className={styles.markerSlot}>
          {sortable && (
            <Button
              ref={handleRef}
              icon="grip-vertical"
              variant="ghost"
              size="sm"
              className={clsx(handleBtnClassname, styles.hoverDragHandle)}
              aria-label={`Reorder ${presentation.ariaLabel}`}
              title="Drag within this target."
            />
          )}
          <span className={styles.marker}>%</span>
        </span>
        <div className={styles.copy}>
          <div className={styles.ellipsis}>{presentation.label}</div>
          {presentation.description && <div className={styles.secondary}>{presentation.description}</div>}
          {presentation.diagnostic && <div className={styles.diagnostic}>{presentation.diagnostic}</div>}
        </div>
      </div>
      {columns.map((column) => (
        <div
          key={column.id}
          role="gridcell"
          className={styles.value}
          style={{ justifyContent: column.align === 'start' ? 'flex-start' : undefined }}
        >
          {column.renderRule(rule)}
        </div>
      ))}
      <div role="gridcell" className={styles.actions}>
        {!readOnly && !disabled && openEdit && (
          <Button
            variant="secondary"
            size="xs"
            icon="pen"
            className={styles.compactButton}
            aria-label={`Edit ${presentation.ariaLabel}`}
            onClick={() => openEdit(node, rule)}
          />
        )}
      </div>
    </div>
  );
}

function GenericBranch<Rule extends DiscountRuleBase, Context = unknown>({
  node,
  depth,
  instance,
  columns,
  presentRule,
  presentBranch,
  inlineRule,
  readOnly,
  disabled,
  openEdit,
  onReorder,
  variant,
}: {
  node: AuthoredDiscountTreeNode<Rule, Context>;
  depth: number;
  instance: string;
  columns: readonly DiscountColumn<Rule, Context>[];
  presentRule: (rule: Rule) => DiscountRulePresentation;
  presentBranch?: DiscountTreeEditorProps<Rule, Context>['presentBranch'];
  inlineRule?: DiscountTreeEditorProps<Rule, Context>['inlineRule'];
  readOnly: boolean;
  disabled: boolean;
  openEdit?: (node: AuthoredDiscountTreeNode<Rule, Context>, rule: Rule) => void;
  onReorder?: DiscountTreeEditorProps<Rule, Context>['onReorder'];
  variant: 'default' | 'attention';
}) {
  const inline = variant === 'attention' ? undefined : inlineRule?.(node);
  const presentation = presentBranch?.(node);
  const rulePresentation = inline ? presentRule(inline) : undefined;
  const separateRules = node.rules.filter((rule) => rule.id !== inline?.id);
  const group = `${instance}:${node.id}`;
  const current = useRef({ node, onReorder });
  current.current = { node, onReorder };
  const sortable = !!onReorder && variant !== 'attention' && !readOnly && !disabled && node.rules.length > 1;
  useDragMonitor(
    (source, target, edge) => {
      const { node: currentNode, onReorder: currentOnReorder } = current.current;
      const from = currentNode.rules[source.index];
      const to = currentNode.rules[target.index];
      if (from && to && edge) void currentOnReorder?.(currentNode, from, to, edge);
    },
    group,
    !sortable,
  );
  return (
    <>
      <div
        role="row"
        aria-level={depth + 1}
        aria-label={node.label}
        className={clsx(styles.row, variant === 'attention' && styles.attentionRow)}
        data-node
        data-tree-row
      >
        <div role="gridcell" className={styles.label} style={{ '--depth': depth } as CSSProperties}>
          <span className={styles.nodeMarker}>{node.kind[0]!.toUpperCase()}</span>
          <div className={styles.copy}>
            <Box fontWeight="semibold" className={styles.ellipsis} title={node.label}>
              {node.label}
              {presentation?.suffix && (
                <Box
                  as="span"
                  fontWeight="regular"
                  color="secondary"
                  textSize="xs"
                  ml="2"
                  title={typeof presentation.suffix === 'string' ? presentation.suffix : undefined}
                >
                  {presentation.suffix}
                </Box>
              )}
            </Box>
            {rulePresentation?.description && <div className={styles.secondary}>{rulePresentation.description}</div>}
            {rulePresentation?.diagnostic && <div className={styles.diagnostic}>{rulePresentation.diagnostic}</div>}
            {node.rules.length > 1 && (
              <Box textSize="xs" color="secondary">
                At this target, the first matching rule with a value for the pricing context wins. More-specific targets
                take priority. Overlap warnings do not prevent saving or reordering.
              </Box>
            )}
          </div>
        </div>
        {columns.map((column) => (
          <div
            key={column.id}
            role="gridcell"
            className={styles.value}
            style={{ justifyContent: column.align === 'start' ? 'flex-start' : undefined }}
          >
            {inline ? column.renderRule(inline) : column.renderBranch?.(node)}
          </div>
        ))}
        <div role="gridcell" className={styles.actions}>
          {!readOnly && !disabled && (
            <>
              {inline && openEdit && (
                <Button
                  variant="secondary"
                  size="xs"
                  icon="pen"
                  className={styles.compactButton}
                  aria-label={`Edit rule: ${node.label}`}
                  title="Edit discount rule"
                  onClick={() => openEdit(node, inline)}
                />
              )}
              {variant !== 'attention' && presentation?.actions}
            </>
          )}
        </div>
      </div>
      {separateRules.map((rule, index) => (
        <GenericRuleRow
          key={rule.id}
          node={node}
          rule={rule}
          index={index}
          depth={depth}
          instanceId={group}
          columns={columns}
          presentRule={presentRule}
          readOnly={readOnly}
          disabled={disabled}
          openEdit={openEdit}
          onReorder={onReorder}
          variant={variant}
        />
      ))}
      {node.children.map((child) => (
        <GenericBranch
          key={child.id}
          node={child}
          depth={depth + 1}
          instance={instance}
          columns={columns}
          presentRule={presentRule}
          presentBranch={presentBranch}
          inlineRule={inlineRule}
          readOnly={readOnly}
          disabled={disabled}
          openEdit={openEdit}
          onReorder={onReorder}
          variant={variant}
        />
      ))}
    </>
  );
}

export function DiscountTreeEditor<Rule extends DiscountRuleBase, Context = unknown>({
  nodes,
  columns,
  presentRule,
  presentBranch,
  inlineRule,
  actionsWidth = '4rem',
  sections,
  emptyText = 'No discount rules yet.',
  readOnly = false,
  disabled = false,
  onEdit,
  createAction,
  onReorder,
}: DiscountTreeEditorProps<Rule, Context>) {
  const instance = useId();
  const canMutate = !readOnly && !disabled;
  const renderSection = (
    section: DiscountTreeSection<Rule, Context> | undefined,
    sectionNodes: readonly AuthoredDiscountTreeNode<Rule, Context>[],
  ) => {
    const sectionInstance = `${instance}:${section?.id ?? 'default'}`;
    return (
      <section key={section?.id ?? 'default'}>
        {section?.title && <h2>{section.title}</h2>}
        {section?.description && <p>{section.description}</p>}
        <div className={styles.tree}>
          <div
            className={styles.grid}
            role="treegrid"
            aria-label={typeof section?.title === 'string' ? section.title : 'Discount rules'}
            style={
              {
                '--discount-columns': `minmax(18rem, 1fr) ${columns.map((column) => column.width ?? '7rem').join(' ')} ${actionsWidth}`,
              } as CSSProperties
            }
          >
            <GenericHeader columns={columns} />
            {sectionNodes.map((node) => (
              <GenericBranch
                key={node.id}
                node={node}
                depth={0}
                instance={sectionInstance}
                columns={columns}
                presentRule={presentRule}
                presentBranch={presentBranch}
                inlineRule={inlineRule}
                readOnly={readOnly}
                disabled={disabled}
                openEdit={onEdit}
                onReorder={onReorder}
                variant={section?.variant ?? 'default'}
              />
            ))}
          </div>
        </div>
      </section>
    );
  };
  const visibleSections = sections
    ? sections
        .map((section) => ({ section, nodes: filterSectionNodes(nodes, section) }))
        .filter(({ nodes: sectionNodes }) => sectionNodes.length > 0)
    : nodes.length
      ? [{ section: undefined, nodes }]
      : [];

  return (
    <>
      {visibleSections.length ? (
        visibleSections.map(({ section, nodes: sectionNodes }) => renderSection(section, sectionNodes))
      ) : (
        <Box p="4" color="secondary" textSize="sm">
          {emptyText}
        </Box>
      )}
      {canMutate && createAction && <div className={styles.footer}>{createAction}</div>}
    </>
  );
}
